import { debounce, getStoreOpenStatus, storageAvailable, getDistanceFromKm, getDistanceFromLatLon, deg2rad } from './utils.js';

// Store locator SVG icons used throughout the component
const storeLocatorIcons = {
  phone: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="Group 5680">
            <path id="Vector"
              d="M13.4382 1.42871H1.02527V7.79996H3.4338L6.33171 10.6979V7.79996H13.4382V1.42871Z"
              stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linejoin="bevel" />
            <path id="Vector_2"
              d="M9.10815 10.6982H16.975V14.7361H15.4485L13.612 16.5727V14.7361H9.10815V10.6982Z"
              stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linejoin="bevel" />
          </g>
        </svg>`,
  map: `<?xml version="1.0" encoding="UTF-8"?><svg id="Layer_2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.5 21.5"><g id="Layer_1-2"><path d="M23.13.1c-.23-.13-.52-.13-.75,0l-6.65,3.8L8.09.08c-.1-.05-.22-.08-.34-.08-.05,0-.1.01-.15.02-.04,0-.08.01-.11.03-.06.02-.11.05-.16.08L.38,4.1c-.24.13-.38.38-.38.65v16c0,.27.14.51.37.65.23.13.52.13.75,0l6.65-3.8,7.56,3.77c.12.08.27.13.42.13.05,0,.1-.01.15-.02.04,0,.08-.01.11-.03.06-.02.11-.05.16-.08l6.95-3.97c.24-.13.38-.38.38-.65V.75c0-.27-.14-.51-.37-.65ZM1.5,5.19l5.5-3.15v14.28l-5.5,3.14V5.19ZM15,19.54l-6.5-3.25V1.96l6.5,3.25v14.33ZM22,16.31l-5.5,3.15V5.18l5.5-3.14v14.27Z"/></g></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
    <path d="M2.66732 3.16699H13.334C14.0673 3.16699 14.6673 3.76699 14.6673 4.50033V12.5003C14.6673 13.2337 14.0673 13.8337 13.334 13.8337H2.66732C1.93398 13.8337 1.33398 13.2337 1.33398 12.5003V4.50033C1.33398 3.76699 1.93398 3.16699 2.66732 3.16699Z" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M14.6673 4.5L8.00065 9.16667L1.33398 4.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  star: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
  <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"/>
</svg>`,
};

// API endpoints for fetching store locations
const locationsApi = `${theme.routes.apiUrl}/locations?unfiltered=true`;

const STORE_SEARCH_MAX_LENGTH = 80;
const STORE_SEARCH_ALLOWED_PATTERN = /^[\p{L}\p{N}\s,.'\-()\/&]+$/u;

function encodeHtml(value) {
  return String(value).replace(/[&<>"'=]/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      case '=':
        return '&#61;';
      default:
        return char;
    }
  });
}

function isValidStoreSearchTerm(value) {
  if (typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return true;

  return (
    trimmed.length <= STORE_SEARCH_MAX_LENGTH && STORE_SEARCH_ALLOWED_PATTERN.test(trimmed)
  );
}

/**
 * Main Store Locator Web Component
 * Handles store selection, geolocation, and displaying store details
 */
if (!customElements.get('my-store-locator')) {
  customElements.define(
    'my-store-locator',
    class MyStoreLocator extends HTMLElement {
      constructor() {
        super();

        // DOM selectors used throughout the component
        this.dom = {
          mobile: 'my-store-locator-mobile',
          btn: '.js-my-store-locator-btn',
          drawer: 'my-store-locator-drawer',
          drawerBtn: '.js-my-store-locator-drawer-btn',
          info: '.js-my-store-locator-info',
          details: '.js-my-store-locator-details',
          close: '.js-my-store-locator-close',
          location: '.js-my-store-locator-location',
          telephone: '.js-my-store-locator-telephone',
          email: '.js-my-store-locator-email',
          address: '.js-my-store-locator-address',
          hours: '.js-my-store-locator-hours',
          services: '.js-my-store-locator-services',
          distance: '.js-my-store-locator-distance',
          pickupAvailability: 'pickup-availability',
          searchButton: '.js-my-store-locator-search-drawer-launcher',
          filterResults: 'ProductGridContainer',
          filterTag: '.js-my-store-pickup-location-text',
          filterInput: '.js-my-store-pickup-location-input',
          storeLocatorSearch: 'storeLocatorSearch',
        };

        this.mobileLocator = document.querySelector(this.dom.mobile);
        this.button = this.querySelector(this.dom.btn);
        this.drawer = document.querySelector(this.dom.drawer);
        this.drawerBtn = this.querySelector(this.dom.drawerBtn);
        if (this.drawerBtn)
          this.drawerBtn.addEventListener('click', this.onClickShowStoreSelector.bind(this));
        this.details = this.querySelector(this.dom.details);
        this.detailsClose = this.details.querySelector(this.dom.close);
        this.detailsClose.addEventListener('click', this.hideDetails.bind(this));
        this.detailsLocation = this.details.querySelector(this.dom.location);
        this.detailsTelephone = this.details.querySelector(this.dom.telephone);
        this.detailsEmail = this.details.querySelector(this.dom.email);
        this.detailsAddress = this.details.querySelector(this.dom.address);
        this.detailsHours = this.details.querySelector(this.dom.hours);
        this.detailsServices = this.details.querySelector(this.dom.services);
        this.distance = this.details.querySelector(this.dom.distance);
        this.filterResults = document.getElementById(this.dom.filterResults) || null;
        this.storeLocatorSearch = document.getElementById(this.dom.storeLocatorSearch) || null;

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onClickToggleDetails = this.onClickToggleDetails.bind(this);
        this.onClickShowStoreSelector = this.onClickShowStoreSelector.bind(this);

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hideDetails();
        });

        if (this && this.filterResults) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation && mutation.type === 'childList') {
                const $searchButtons = document.querySelectorAll(this.dom.searchButton);
                $searchButtons.forEach(($searchButton) => {
                  $searchButton.addEventListener('click', this.onClickShowStoreSelector.bind(this));
                });

                this.updateFacets(this.button.querySelector(this.dom.info).innerHTML);
              }
            });
          });

          // watch for changes to the filters and add the event listeners
          observer.observe(this.filterResults, { childList: true });
        }

        const $searchButtons = document.querySelectorAll(this.dom.searchButton);
        $searchButtons.forEach(($searchButton) => {
          $searchButton.addEventListener('click', this.onClickShowStoreSelector.bind(this));
        });

        // Initialize component state
        this.preferredStoreId = null;
        this.myCoords = null;
        this.allLocationsData = [];
        this.itemLocationsData = {};
        this.storeSuggestions = [];

        this.init();

        window.refreshStoreLocator = this.init.bind(this);

        if (storageAvailable('localStorage')) {
          const preferredStoreId = localStorage.getItem('preferredStoreId');
          if (preferredStoreId) {
            this.preferredStoreId = preferredStoreId;
          }
        }
      }

      /**
       * Checks if browser storage (localStorage/sessionStorage) is available
       * @param {string} type - Storage type to check ('localStorage' or 'sessionStorage')
       * @returns {boolean} Whether storage is available
       */
      storageAvailable(type) {
        let storage;
        try {
          storage = window[type];
          const x = '__storage_test__';
          storage.setItem(x, x);
          storage.removeItem(x);
          return true;
        } catch (e) {
          return (
            e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
              // Firefox
              e.code === 1014 ||
              // test name field too, because code might not be present
              // everything except Firefox
              e.name === 'QuotaExceededError' ||
              // Firefox
              e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage &&
            storage.length !== 0
          );
        }
      }

      /**
       * Initialize the store locator
       * Handles geolocation permissions and fetches initial store data
       */
      init() {
        const expiry = storeLocator.Data.locationCookieExpiry || 30;

        // Reset preferredStoreId at initialization
        this.preferredStoreId = null;

        // Only set preferredStoreId if it exists in localStorage
        if (this.storageAvailable('localStorage')) {
          const storedPreferredStoreId = localStorage.getItem('preferredStoreId');
          if (storedPreferredStoreId) {
            this.preferredStoreId = storedPreferredStoreId;
          }
        }

        /**
         * Sets a secure cookie with specified name, value and expiry
         */
        function setSecureCookie(name, value, days) {
          var expires = '';

          if (days) {
            var date = new Date();
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
            expires = '; expires=' + date.toUTCString();
          }
          document.cookie = name + '=' + (value || '') + expires + '; path=/' + '; secure';
        }

        /**
         * Retrieves cookie value by name
         */
        function getCookie(name) {
          var nameEQ = name + '=';
          var ca = document.cookie.split(';');

          for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) == ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
          }

          return null;
        }

        function accept(pos, cookie = false) {
          if (cookie && pos) {
            const position = {
              coords: {
                latitude: pos && pos.coords ? pos.coords.latitude : null,
                longitude: pos && pos.coords ? pos.coords.longitude : null,
              },
            };

            setSecureCookie('location_sharing', 'true', expiry);
            setSecureCookie('location_position', JSON.stringify(position), expiry);
          }
          this.fetchLocations(pos);
        }

        function decline(cookie = false) {
          if (cookie) setSecureCookie('location_sharing', 'false', expiry);

          this.fetchLocations(null, true, () => {
            const hasUpdated = this.updateMyStoreFromLocalStorage();
            if (!hasUpdated) {
              // Show select store message instead of error when user declines location
              this.renderSelectYourStore(this.dataset.locationDeniedSelectText || this.dataset.selectText);
            }
          });
        }

        function getLocation() {
          const enableCookie = storeLocator.Data.locationCookie || false;

          if (enableCookie) {
            const locationPreference = getCookie('location_sharing') || null;

            if (locationPreference && locationPreference === 'false') {
              decline.call(this, false);

              this.hasInitialized = true;
              return;
            }

            if (locationPreference && locationPreference === 'true') {
              const locationPosition = getCookie('location_position') || null;

              if (locationPosition) {
                const pos = JSON.parse(locationPosition);

                accept.call(this, pos, true);
                this.hasInitialized = true;
                return;
              }
            }

            navigator.geolocation.getCurrentPosition(
              (pos) => accept.call(this, pos, true),
              decline.bind(this),
            );
            this.hasInitialized = true;

            return;
          }

          navigator.geolocation.getCurrentPosition(accept.bind(this), decline.call(this, false));
          this.hasInitialized = true;
        }

        if (this.storageAvailable('localStorage')) {
          const preferredStoreId = localStorage.getItem('preferredStoreId');
          if (preferredStoreId) {
            this.preferredStoreId = preferredStoreId;
          }
        }

        if (!navigator.geolocation) {
          this.fetchLocations(null, true, () => {
            const hasUpdated = this.updateMyStoreFromLocalStorage();
            if (!hasUpdated) {
              // Show select store message instead of error when user declines location
              this.renderSelectYourStore(this.dataset.locationDeniedSelectText || this.dataset.selectText);
            }
          });
        } else {
          getLocation.call(this);
        }
      }

      /**
       * Updates the store filter facets in collection pages
       * @param {string} placeholderText - Text to show when no store is selected
       */
      updateFacets(placeholderText = null) {
        if (!this || !this.filterResults) return;
        const $placeholder = document.querySelector(
          `${this.dom.filterInput}[data-location-id="placeholder"]`,
        );
        // Fix for the optional chaining error - use traditional null check
        const $placeholderButton = $placeholder ? $placeholder.closest('li').querySelector(this.dom.searchButton) : null;
        if (placeholderText && $placeholderButton) {
          $placeholderButton.innerText = placeholderText;
        }

        const $prevInput = document.querySelector(`${this.dom.filterInput}.active`);
        const $prevInputChecked = $prevInput ? $prevInput.checked : false;
        let $visibleInput = null;

        this.allLocationsData.forEach((location) => {
          const $filterTags = document.querySelectorAll(
            `${this.dom.filterTag}[data-location-id="${location.id}"]`,
          );
          $filterTags.forEach(($filterTag) => {
            $filterTag.innerText = location.name;
          });

          const $filterInputs = document.querySelectorAll(this.dom.filterInput);
          $filterInputs.forEach(($filterInput) => {
            const pickupLocationId = $filterInput.dataset?.locationId;

            // update pickup names
            if (pickupLocationId === location.id) {
              $filterInput.innerText = location.name;
              const $searchButton = $filterInput.closest('li').querySelector(this.dom.searchButton);
              if ($searchButton) {
                $searchButton.innerText = location.name;
              }
            }
            // set preferred store as visible
            if (pickupLocationId === this.preferredStoreId) {
              $visibleInput = $filterInput;
              $filterInput.disabled = false;
              $filterInput.classList.add('active');
              $filterInput.closest('label').classList.remove('facet-checkbox--disabled');
              $filterInput.closest('li').classList.add('active');
            } else if (pickupLocationId !== 'placeholder') {
              $filterInput.disabled = true;
              $filterInput.checked = false;
              $filterInput.classList.remove('active');
              $filterInput.closest('label').classList.add('facet-checkbox--disabled');
              $filterInput.closest('li').classList.remove('active');
            }
          });
        });

        if (
          $visibleInput &&
          $prevInput &&
          $visibleInput?.id !== $prevInput?.id &&
          $prevInputChecked
        ) {
          // simulate click to update the active state
          // if the previous preferred store was checked
          const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          });
          $visibleInput.dispatchEvent(clickEvt);
        }

        // show placeholder if preferred store is not an available filter
        if (!$visibleInput && $placeholder) {
          $placeholder.closest('li').classList.add('active');

          //clear active location filter
          const $filterTag = document.querySelector(`${this.dom.filterTag}[data-location-id]`);
          if ($filterTag) {
            const clickEvt = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            $filterTag.closest('a').dispatchEvent(clickEvt);
          }
        } else if ($placeholder) {
          $placeholder.closest('li').classList.remove('active');
        }
      }

      /**
       * Updates the preferred store from localStorage if available
       * @returns {boolean} Whether a store was successfully loaded from storage
       */
      updateMyStoreFromLocalStorage() {
        let hasUpdated = false;
        if (this.storageAvailable('localStorage')) {
          const preferredStoreId = localStorage.getItem('preferredStoreId');
          if (preferredStoreId) {
            const preferredLocation = this.allLocationsData.find(
              (location) => location.id === preferredStoreId,
            );
            if (preferredLocation) {
              this.updateMyStoreWithLocation(preferredLocation);
              hasUpdated = true;
            } else {
              // If stored ID doesn't match any location, clear it
              localStorage.removeItem('preferredStoreId');
              this.preferredStoreId = null;
            }
          }
        }
        return hasUpdated;
      }

      /**
       * Builds GraphQL query for fetching store locations
       * @param {Array} productQueryIds - Product IDs to check availability for
       * @returns {string} GraphQL query
       */
      locationsQuery(productQueryIds = []) {
        const locationQuery = `
          id
          name
          address {
            formatted
            address1
            city
            province
            provinceCode
            zip
            country
            phone
            latitude
            longitude
          }
          phoneOverride: metafield(namespace: "custom", key: "apl_PhoneOverride") {
            value
          }
          storePage: metafield(namespace: "custom", key: "store") {
            reference {
              ... on Metaobject {
                handle
                type
                fields {
                  key
                  value
                }
              }
            }
          }
          image: metafield(namespace: "custom", key: "store_image") {
            reference {
              ... on MediaImage {
                image {
                  originalSrc
                }
              }
            }
          }
        `;
        const getNearestQuery = `
          getNearestPickupLocations($first: Int!, $location: GeoCoordinateInput, $sortKey: LocationSortKeys) @inContext(language: ${
            storeLocator?.Data?.language || 'NL'
          }) {
            locations(near: $location, first: $first, sortKey: $sortKey) {
              edges {
                node {
                  ${locationQuery}
                }
              }
            }
          }
        `;
        if (productQueryIds.length > 0) {
          let productsQuery = '';
          let productIds = '';
          let selectedOptions = '';
          const activeLang = storeLocator?.Data?.language || 'EN';

          productQueryIds.forEach((key) => {
            productIds += `$productId${key}: ID!,`;
            selectedOptions += `$selectedOptions${key}: [SelectedOptionInput!]!,`;
            productsQuery += `
              product${key}: product(id: $productId${key}) {
                variantBySelectedOptions(selectedOptions: $selectedOptions${key}) {
                  storeAvailability(first: $first) {
                    edges {
                      node {
                        available
                        pickUpTime
                        quantityAvailable
                        location {
                          ${locationQuery}
                        }
                      }
                    }
                  }
                }
              }
            `;
          });
          return `
            query Products(${productIds} ${selectedOptions} $first: Int!, $location: GeoCoordinateInput, $sortKey: LocationSortKeys) @inContext(language: ${activeLang}){
              ${productsQuery}
              locations(near: $location, first: $first, sortKey: $sortKey) {
                edges {
                  node {
                    ${locationQuery}
                  }
                }
              }
            }
          `;
        }
        return `
          query ${getNearestQuery}
        `;
      }

      /**
       * Fetches store locations from Fashion Society API
       * @param {Object} position - Geolocation coordinates
       * @param {boolean} setDataOnly - Whether to only update data without UI
       * @param {Function} cb - Callback function after fetch
       */
      async fetchLocations(position = null, setDataOnly = false, cb = null) {
        try {
          const response = await fetch(locationsApi);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const locationsData = await response.json();

          if (locationsData && Array.isArray(locationsData.locations)) {
            // Process locations data
            this.allLocationsData = locationsData.locations.map(location => {
              const exceptionalOpens = location.store?.reference?.fields?.find(f => f.key === 'exceptionally_opens')?.value;
              const processedLocation = {
                id: location.id,
                name: location.name,
                address: {
                  formatted: location.address?.formatted || [],
                  address1: location.address?.address1,
                  city: location.address?.city,
                  province: location.address?.province,
                  provinceCode: location.address?.provinceCode,
                  zip: location.address?.zip,
                  country: location.address?.country,
                  phone: location.address?.phone,
                  latitude: location.address?.latitude,
                  longitude: location.address?.longitude
                },
                // Add email from store reference fields
                email: { value: location.store?.reference?.fields?.find(f => f.key === 'store_email')?.value },

                // Add phone override from store reference fields
                phoneOverride: {
                  value: location.store?.reference?.fields?.find(f => f.key === 'store_phone_number')?.value
                },

                // Add exceptional opens from store reference fields
                exceptionalOpens: location.store?.reference?.fields?.find(f => f.key === 'exceptionally_opens')?.value,

                // Process opening hours
                hours: this.processOpeningHours(location, exceptionalOpens),

                // Add store page URL from store reference handle
                // if url contains /nl, add /nl to the url
                storePageUrl: location.store?.reference?.handle ? `${window.location.pathname.includes('/fr') ? '/fr' : ''}/pages/${location.store.reference.type.toLowerCase()}/${location.store.reference.handle}` : null,

                // Add store image if available
                image_url: location.image?.reference?.image?.originalSrc,

                // Add store services if available
                services_html: location.services_html
              };

              // Add distance if coordinates are available
              if (position?.coords) {
                this.myCoords = position.coords;
                processedLocation.distance = this.calculateStoreDistance(processedLocation.address);
              }

              return processedLocation;
            });

            if (!setDataOnly) {
              // Don't automatically select first store if no preference exists
              const hasUpdated = this.updateMyStoreFromLocalStorage();
              if (!hasUpdated) {
                // Instead of selecting the first store, just render the "select your store" message
                this.renderSelectYourStore();
              }
            }

            if (cb) {
              cb();
            }
          }
        } catch (error) {
          console.error('Error fetching locations:', error);
          this.renderError();
        }
      }

      // Helper function to process opening hours
      processOpeningHours(location, exceptionalOpens) {
        const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const today = new Date().getDay();
        const d = new Date();
        const time = parseFloat(`${d.getHours()}.${String(d.getMinutes()).padStart(2, '0')}`);
        const todayDate = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;

        // Get exceptional closures from store reference fields
        const storeFields = (location.store && location.store.reference && location.store.reference.fields) ? location.store.reference.fields : [];
        const exceptionalClosures = storeFields.find(f => f.key === 'exceptionally_closed');
        const exceptionalClosuresValue = exceptionalClosures ? exceptionalClosures.value : null;

        // Ensure exceptionalOpens is an array
        if (typeof exceptionalOpens === 'string') {
          try {
            exceptionalOpens = JSON.parse(exceptionalOpens);
          } catch (e) {
            exceptionalOpens = exceptionalOpens ? [exceptionalOpens] : [];
          }
        } else if (!Array.isArray(exceptionalOpens)) {
          exceptionalOpens = [];
        }

        // Ensure exceptionalClosures is an array
        let exceptionalClosuresArray = [];
        if (typeof exceptionalClosuresValue === 'string') {
          try {
            exceptionalClosuresArray = JSON.parse(exceptionalClosuresValue);
          } catch (e) {
            exceptionalClosuresArray = exceptionalClosuresValue ? [exceptionalClosuresValue] : [];
          }
        } else if (Array.isArray(exceptionalClosuresValue)) {
          exceptionalClosuresArray = exceptionalClosuresValue;
        }

        // First check if store is exceptionally closed today
        let isExceptionallyClosed = false;
        exceptionalClosuresArray.forEach(closedDate => {
          const cleanClosedDate = closedDate.replace(/\s+/g, '');
          if (cleanClosedDate === todayDate) {
            isExceptionallyClosed = true;
          }
        });

        if (isExceptionallyClosed) {
          return {
            value: 'exceptionally_closed',
            isOpen: false,
            closingTime: null
          };
        }

        // Check for exceptional opening hours
        let isExceptionallyOpen = false;
        let exceptionalClosingTime = null;

        exceptionalOpens.forEach(exceptionalOpen => {
          // Handle both formats: "date | time" and "date|time"
          const parts = exceptionalOpen.includes(' | ') ?
            exceptionalOpen.split(' | ') :
            exceptionalOpen.split('|').map(s => s.trim());

          const [date, period] = parts;

          // Clean the date string and handle different formats
          let cleanDate = date.replace(/\s+/g, '').replace(/[\[\]"]/g, '');

          // If the date contains day names or month names, extract just the DD/MM/YY part
          if (cleanDate.includes('/')) {
            // Extract the date part that matches DD/MM/YY format
            const dateMatch = cleanDate.match(/(\d{1,2}\/\d{1,2}\/\d{2})/);
            if (dateMatch) {
              cleanDate = dateMatch[1];
            }
          }

          if (cleanDate === todayDate) {
            // Handle both formats: "start - end" and "start-end"
            const timeParts = period.includes(' - ') ?
              period.split(' - ') :
              period.split('-').map(s => s.trim());

            const [start, end] = timeParts;
            const startTime = parseFloat(start.replace(':', '.'));
            const endTime = parseFloat(end.replace(':', '.'));

            if (time >= startTime && time <= endTime) {
              isExceptionallyOpen = true;
              exceptionalClosingTime = end;
            }
          }
        });

        if (isExceptionallyOpen) {
          return {
            value: `exceptional: ${exceptionalClosingTime}`,
            isOpen: true,
            closingTime: exceptionalClosingTime
          };
        }

        // Try to get hours from store reference fields first
        const todayHoursField = storeFields.find(f => f.key === `opening_hours_${daysMap[today]}`);
        const todayHours = todayHoursField ? todayHoursField.value : location[`opening_hours_${daysMap[today]}`];

        if (todayHours) {
          const formattedHours = todayHours.replace(/\s/g, '').replace(/:/g, '.');
          const { isOpen, closingTime } = getStoreOpenStatus(formattedHours);

          // Get opening time for closed stores
          let openingTime = null;
          if (!isOpen) {
            if (formattedHours.includes('&')) {
              const [period1, period2] = formattedHours.split('&');
              const [start1] = period1.split('-');
              const [start2] = period2.split('-');
              openingTime = parseFloat(start1) < parseFloat(start2) ? start1 : start2;
            } else {
              const [start] = formattedHours.split('-');
              openingTime = start;
            }
          }

          return {
            value: formattedHours,
            isOpen: isOpen,
            closingTime: closingTime,
            openingTime: openingTime
          };
        }

        return null;
      }

      serializeRichText(richText) {
        let html = '';
        switch (richText.type) {
          case 'root':
            richText.children.forEach((child) => {
              html += this.serializeRichText(child);
            });
            break;
          case 'list':
            html += `<ul>`;
            richText.children.forEach((child) => {
              html += this.serializeRichText(child);
            });
            html += `</ul>`;
            break;
          case 'list-item':
            html += `<li>`;
            richText.children.forEach((child) => {
              html += this.serializeRichText(child);
            });
            html += `</li>`;
            break;
          case 'text':
            html += richText.value;
            break;
          case 'link':
            html += `<a href="${richText.url}">`;
            richText.children.forEach((child) => {
              html += this.serializeRichText(child);
            });
            html += `</a>`;
            break;
          case 'paragraph':
            html += `<p>`;
            richText.children.forEach((child) => {
              html += this.serializeRichText(child);
            });
            html += `</p>`;
            break;
          default:
            break;
        }
        return html;
      }

      onClickRefreshList(evt) {
        evt.currentTarget.disabled = true;
        this.init();
      }

      onClickToggleDetails() {
        if (this.details.hasAttribute('open')) {
          this.hideDetails();
        } else {
          this.showDetails();
        }
      }

      onClickShowStoreSelector(evt) {
        document.querySelector('my-store-locator-drawer').show(evt.currentTarget);
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this.details &&
          !target.closest(this.dom.btn) &&
          !target.closest(this.dom.details)
        ) {
          this.hideDetails();
        }
      }

      hideDetails() {
        this.details.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
      }

      showDetails(focusElement) {
        this.focusElement = focusElement;
        this.details.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
      }

      removeButtonEvents() {
        this.button.removeEventListener('click', this.onClickRefreshList);
        this.button.removeEventListener('click', this.onClickToggleDetails);
        this.button.removeEventListener('click', this.onClickShowStoreSelector);
      }

      renderError() {
        this.removeButtonEvents();
        this.button.disabled = false;

        // Improve error message with a more helpful text when location access is denied
        const errorMessage = navigator.geolocation ?
          this.dataset.locationDeniedText || this.dataset.errorText :
          this.dataset.locationUnavailableText || this.dataset.errorText;

        this.button.querySelector(this.dom.info).innerHTML = errorMessage;
        this.button.addEventListener('click', this.onClickRefreshList);

        if (this.mobileLocator) {
          this.mobileLocator.renderError();
        }

        const pickupAvailabilities = document.querySelectorAll(this.dom.pickupAvailability);
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderError();
          });
        }

        this.updateFacets(errorMessage);
      }

      renderSelectYourStore(customText = null) {
        this.removeButtonEvents();
        this.button.disabled = false;

        // Use custom text if provided, otherwise use default select text
        const displayText = customText || this.dataset.selectText;
        this.button.querySelector(this.dom.info).innerHTML = displayText;
        this.button.addEventListener('click', this.onClickShowStoreSelector);

        this.drawer.loading.classList.add('hide');

        // Clear preferredStoreId to ensure consistency
        this.preferredStoreId = null;

        // Make sure this is cleared from localStorage too
        if (this.storageAvailable('localStorage')) {
          localStorage.removeItem('preferredStoreId');
        }

        if (this.mobileLocator) {
          this.mobileLocator.renderSelectYourStore(customText);
        }

        const pickupAvailabilities = document.querySelectorAll(this.dom.pickupAvailability);
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderPreview();
          });
        }

        this.updateFacets(displayText);
      }

      updateMyStoreWithLocation(location) {
        if (!location) return;
        this.removeButtonEvents();
        const { name, address, email, hours, phoneOverride, id, distance } = location;
        this.preferredStoreId = id;
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = name;
        this.button.addEventListener('click', this.onClickToggleDetails);

        // Update distances for all locations based on this store's location
        if (address?.latitude && address?.longitude) {
          // Use this store's location as the new center point for distance calculations
          const storePosition = {
            coords: {
              latitude: address.latitude,
              longitude: address.longitude
            }
          };

          // Recalculate distances for all locations
          this.allLocationsData.forEach(loc => {
            if (loc.address?.latitude && loc.address?.longitude) {
              loc.distance = this.calculateStoreDistance(loc.address, storePosition.coords);
            }
          });
        }

        function resetValue(element, attribute = false) {
          if (!element) return;

          element.value = '';
          if (attribute) {
            element.setAttribute(attribute, '');
          }
        }

        this.updateFacets(name);

        this.details.querySelector('h2').innerHTML = name;

        // Disable city for now
        // this.detailsLocation.innerHTML = address?.city && address?.provinceCode ? (`${address.city}, ${address.provinceCode}`) : (`${address.city}`);

        if (address?.phone && address?.phone !== '') {
          this.detailsTelephone.classList.remove('hide');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${address.phone}`;
          phoneLink.innerHTML = address.phone;
        } else {
          const phoneLink = this.detailsTelephone.querySelector('a');
          resetValue(phoneLink, 'href');
          this.detailsTelephone.classList.add('hide');
        }
        if (phoneOverride?.value && phoneOverride?.value !== '') {
          this.detailsTelephone.classList.remove('hide');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${phoneOverride.value}`;
          phoneLink.innerHTML = phoneOverride.value;
        }
        if (address?.address1 && address?.address1 !== '') {
          this.detailsAddress.classList.remove('hide');
          const addressLink = this.detailsAddress.querySelector('a');

          const encodedName = encodeURIComponent(name);
          const latitude = address.latitude;
          const longitude = address.longitude;
          const href = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${latitude},${longitude}`;
          addressLink.href = href;
          addressLink.innerHTML = address.address1;
        }
        if (email?.value && email?.value !== '') {
          this.detailsEmail.classList.remove('hide');
          const emailLink = this.detailsEmail.querySelector('a');
          emailLink.href = `mailto:${email.value}`;
          emailLink.innerHTML = email.value;
        } else {
          const emailLink = this.detailsEmail.querySelector('a');
          resetValue(emailLink, 'href');
          this.detailsEmail.classList.add('hide');
        }
        if (hours?.value && hours?.value !== '') {
          this.detailsHours.classList.remove('hide');
          const hoursSpan = this.detailsHours.querySelector('span');
          hoursSpan.innerHTML = hours.value;
        } else {
          const hoursSpan = this.detailsHours.querySelector('span');
          resetValue(hoursSpan);
          this.detailsHours.classList.add('hide');
        }
        if (location?.services_html) {
          this.detailsServices.classList.remove('hide');
          this.detailsServices.innerHTML = `
            <button class="my-location-result__services-btn js-acc-button" type="button">
              <span>${
                storeLocator?.Data?.storeServiceTrigger
                  ? storeLocator.Data.storeServiceTrigger
                  : 'View store services'
              }</span>
              <i class="js-acc-icon myarrow fa fa-chevron-down my-location-result__services-icon" aria-hidden="true"></i>
            </button>
            <div class="my-location-result__services js-acc-details" aria-expanded="false">${
              location.services_html
            }</div>
          `;
          const $accButton = this.detailsServices.querySelector('.js-acc-button');
          const $accDetails = this.detailsServices.querySelector('.js-acc-details');
          const $accIcon = this.detailsServices.querySelector('.js-acc-icon');

          function openAcc(el) {
            el.setAttribute('aria-expanded', true);
            el.classList.add('is-active');
          }

          function closeAcc(el) {
            el.setAttribute('aria-expanded', false);
            el.classList.remove('is-active');
          }

          $accButton.addEventListener('click', () => {
            if ($accDetails.classList.contains('is-active')) {
              closeAcc($accDetails);
              if ($accIcon) closeAcc($accIcon);
            } else {
              openAcc($accDetails);
              if ($accIcon) openAcc($accIcon);
            }
          });
        } else {
          this.detailsServices.classList.add('hide');
        }

        this.updateMyStoreDistance(distance);

        if (this.mobileLocator) {
          this.mobileLocator.updateMyStoreWithLocation(location);
        }

        const pickupAvailabilities = document.querySelectorAll(this.dom.pickupAvailability);

        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderPreview(location);
          });
        }

        // Update facets and refresh any related displays
        this.updateFacets(name);

        // Update any open drawers
        if (this.drawer && this.drawer.isOpen) {
          this.drawer.updateStoreDetails(this.allLocationsData, '');
        }
      }

      updateMyStoreDistance(distance) {
        if (distance) {
          this.distance.innerHTML = distance;
          this.distance.classList.remove('hide');
          if (this.mobileLocator?.distance) {
            this.mobileLocator.distance.innerHTML = distance;
            this.mobileLocator.distance.classList.remove('hide');
          }
        } else {
          this.distance.classList.add('hide');
          if (this.mobileLocator?.distance) {
            this.mobileLocator.distance.classList.add('hide');
          }
        }
      }

      calculateStoreDistance(locationAddress, customCoords = null) {
        const sourceCoords = customCoords || this.myCoords;

        if (
          sourceCoords?.latitude &&
          sourceCoords?.longitude &&
          locationAddress?.latitude &&
          locationAddress?.longitude
        ) {
          return getDistanceFromLatLon(
            sourceCoords.latitude,
            sourceCoords.longitude,
            locationAddress.latitude,
            locationAddress.longitude,
            this.dataset.distanceUnit,
            this.dataset.distanceKmLabel,
            this.dataset.distanceMilesLabel
          );
        } else {
          return null;
        }
      }

      /**
       * Fetch data from Shopify Storefront API
       */
      async fetchStorefrontApi(query, variables = {}) {

        const endpoint = `https://${Shopify.shop}/api/2024-07/graphql.json`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.dataset.storefrontApiAccessToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        const { data, errors } = await response.json();

        if (errors) {
          this.renderError();
          throw new Error(errors[0].message);
        }

        return data;
      }

      updateStoreDetails(locationData, nearbyValue) {
        let resultCount = 0;
        const { mapPage } = storeLocator.Data || false;

        this.results.innerHTML = '';

        let filteredLocations = locationData;
        const hasDistanceData = locationData.some((location) => location.distance);

        if (this.resultsDistance.value !== 'all' && hasDistanceData) {
          const selectedDistance = parseFloat(this.resultsDistance.value);
          filteredLocations = locationData.filter((location) => {
            if (!location.distance) return false;
            const locationDistance = parseFloat(location.distance.split(' ')[0]);
            return locationDistance <= selectedDistance;
          });
        }

        // Sort locations by distance if distance data is available
        if (hasDistanceData) {
          filteredLocations.sort((a, b) => {
            const distanceA = a.distance ? parseFloat(a.distance.split(' ')[0]) : Infinity;
            const distanceB = b.distance ? parseFloat(b.distance.split(' ')[0]) : Infinity;
            return distanceA - distanceB;
          });
        } else if (!nearbyValue) {
          if (this.storeLocator.preferredStoreId) {
            // If preferred store exists, move it to top
            const activeIndex = filteredLocations.findIndex(
              (location) => location.id === this.storeLocator.preferredStoreId
            );
            if (activeIndex > -1) {
              filteredLocations.splice(0, 0, filteredLocations.splice(activeIndex, 1)[0]);
            }
          } else {
            // If no preferred store, sort alphabetically by name
            filteredLocations.sort((a, b) => a.name.localeCompare(b.name));
          }
        }

        if (window.showLocation) {
          window.showLocation(null, filteredLocations);
        }

        function setCityAndStateHTML(location) {
          const { address } = location || null;
          let html;

          if (!address) return;

          if (address?.city && address?.provinceCode) {
            html = `${address.city}, ${address.provinceCode}`;
            return html;
          }

          if (address?.city && !address?.provinceCode) {
            html = `${address.city}`;
            return html;
          }
        }

        filteredLocations.forEach((location) => {
          const isActive = this.storeLocator.preferredStoreId && location.id === this.storeLocator.preferredStoreId;
          const distanceHidden = location.distance ? '' : ' hidden';

          let availabilityHtml = '';
          if (location.available) {
            let stockHtml = '';

            let pickUpTimeTranslated = '';
            if (location.pickUpTime) {
              const key = location.pickUpTime;
              const languageKey = storeLocator.Data.language?.toLowerCase() || 'en';
              pickUpTimeTranslated =
                window.deliveryOptionsStrings?.[languageKey]?.[key] || location.pickUpTime;
            }

            if (location.quantityAvailable < 10) {
              const stockText = this.storeLocator.dataset.lowStockHtml.replace(
                '{{ quantity }}',
                location.quantityAvailable,
              );
              stockHtml = `
                <div class="my-location-result__availability-stock ">${stockText}</div>
              `;
            }
            availabilityHtml = `
              <div class="my-location-result__availability">
                <div class="my-location-result__availability-time">${pickUpTimeTranslated}</div>
                ${stockHtml}
              </div>
            `;
          } else if (location.available === false) {
            availabilityHtml = `
              <div class="my-location-result__availability">
                ${this.storeLocator.dataset.unavailableText}
              </div>
            `;
          }
          let itemKeyAttr = '';
          if (location.itemKey) {
            itemKeyAttr = `data-item-key="${location.itemKey}"`;
          }
          let resultHtml;
          const { address, name } = location || null;
          if (!address) return;
          const { latitude, longitude } = address || null;

          let locationImageHtml = '';
          let serviceHtml = '';
          let telephoneVal = null;
          let calloutHref = null;
          let addressHref = null;

          if (location?.services_html) {
            serviceHtml = `
                <button class="my-location-result__services-btn js-acc-button" type="button">
                  <span>${
                    storeLocator?.Data?.storeServiceTrigger
                      ? storeLocator.Data.storeServiceTrigger
                      : 'View store services'
                  }</span>
                  <i class="myarrow fa fa-chevron-down my-location-result__services-icon js-acc-icon" aria-hidden="true"></i>
                </button>
                <div class="my-location-result__services js-acc-details" aria-expanded="false">${
                  location.services_html
                }</div>
              `;
          }

          if (location?.address?.phone) {
            telephoneVal = location.address.phone;
          }

          if (location?.phoneOverride?.value) {
            telephoneVal = location.phoneOverride.value;
          }

          const encodedName = encodeURIComponent(name);
          addressHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${address.latitude},${address.longitude}`;
          calloutHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${address.latitude},${address.longitude}`;

          // Build the location result HTML with store details, including distance, address, phone, and services
            resultHtml = `
            <div
              class="js-my-location-result my-location-result"
              data-active="${isActive}"
              data-id="${location.id}"
              data-name="${name}"
              data-latitude="${latitude}"
              data-longitude="${longitude}"
              aria-role="button"
              ${itemKeyAttr}
            >
              <div class="js-my-location-result-distance my-store-locator__details-distance">
                <div class="icon">
                  ${storeLocatorIcons.map}
                </div>
                ${location.distance ? location.distance : ''}
              </div>

              <div class="">
                <div class="my-location-result__my-store">${storeLocator.Data.myStore}</div>
                <section class="js-my-results-details">
                  <div class="my-location-result__details">
                    <div class="my-location-result__name">${location.name}</div>

                    </div>

                    <ul class="list-unstyled my-location-result__business">
                      ${
                        location?.address?.address1
                          ? `<li class="my-location-result__address">
                          <a href="${calloutHref}" target="_blank">
                            ${location.address.address1}${
                              location.address.zip ? `, ${location.address.zip}` : ''
                            } ${location.address.city}
                          </a>
                        </li>`
                          : ''
                      }
                      ${
                        telephoneVal || location?.email?.value
                          ? `<li class="my-location-result__contact-row my-location-result__address">
                            ${
                              telephoneVal
                                ? `<a class="my-location-result__contact-item" href="tel:${telephoneVal}">
                                    <div class="icon icon-phone">
                                      ${storeLocatorIcons.phone}
                                    </div>
                                    <span>${telephoneVal}</span>
                                  </a>`
                                : ''
                            }
                          </li>`
                          : ''
                      }
                      ${
                        location.hours?.value
                          ? `<li class="my-location-result__address ${
                              location.hours.isOpen ? 'store--open' : 'store--closed'
                            }">
                          <span class="store-status-indicator"></span>
                          <span>
                            ${
                              location.hours.isOpen
                                ? location.hours.value.startsWith('exceptional:')
                                  ? `${storeLocator.Data.nowExceptionallyOpen || 'Now Exceptionally Open'} ${
                                      storeLocator.Data.untilText || 'until'
                                    } ${location.hours.closingTime}`
                                  : `${storeLocator.Data.nowOpen || 'Now Open'} ${
                                      storeLocator.Data.untilText || 'until'
                                    } ${location.hours.closingTime}`
                                : location.hours.value === 'closed' || location.hours.value === 'gesloten' || location.hours.value === 'fermé'
                                  ? `${storeLocator.Data.nowClosed || 'Now Closed'}`
                                  : location.hours.value === 'exceptionally_closed'
                                    ? `${storeLocator.Data.exceptionallyClosedToday || 'Exceptionally Closed Today'}`
                                    : `${storeLocator.Data.nowClosed || 'Now Closed'} – ${
                                        storeLocator.Data.opensAt || 'opens at'
                                      } ${location.hours.openingTime}`
                            }
                          </span>
                        </li>`
                          : ''
                      }
                    </ul>

                  </section>
                  ${serviceHtml}

                   <div class="my-location-result__link js-my-location-link">

                     ${
                       location.storePageUrl
                         ? `
                          <a href="${location.storePageUrl}" class="button button--secondary">
                            <span data-button-animate-chars="" class="button__text">
                              ${storeLocator.Data.viewStorePage || 'View Store Page'}
                            </span>

                            <div class="icon icon-arrow-right">
                              ${theme.icons.arrowRightTopSmall}
                            </div>
                          </a>
                  `
                         : ''
                     }

                      <a href="${calloutHref}" target="_blank" rel="noopener noreferrer">
                          <div class="icon">
                            ${storeLocatorIcons.map}
                          </div>
                      </a>
                    </div>

                 <button class="js-make-my-store-btn my-location-result__make-my-store-btn ${isActive ? 'active' : ''}">
                    <div class="icon icon--star">
                      ${storeLocatorIcons.star}
                    </div>
                  </button>
              </div>


            </div>`;
            const resultNode = new DOMParser().parseFromString(resultHtml, 'text/html').body
              .childNodes[0];
            resultNode.querySelector('.js-make-my-store-btn').addEventListener('click', (e) => {
              const activeResult = this.querySelector(this.dom.activeResult);
              if (activeResult) {
                activeResult.dataset.active = 'false';
              }
              e.currentTarget.parentNode.dataset.active = 'true';

              this.selectPreferredLocation();
            });

            this.results.append(resultNode);
            resultCount++;

          // Add event listeners for the newly created elements
          const makeMyStoreBtn = this.results.querySelector(
            `[data-id="${location.id}"] .js-make-my-store-btn`,
          );
          if (makeMyStoreBtn) {
            makeMyStoreBtn.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();

              // Check if this is already the preferred store
              if (this.storeLocator.preferredStoreId && location.id === this.storeLocator.preferredStoreId) {
                // Clear the preferred store state
                if (this.storeLocator.storageAvailable('localStorage')) {
                  localStorage.removeItem('preferredStoreId');
                }

                // Clear the ID in memory
                this.storeLocator.preferredStoreId = null;

                // Update visual state
                const storeElement = this.results.querySelector(`[data-id="${location.id}"]`);
                if (storeElement) {
                  storeElement.dataset.active = 'false';
                }

                // Update UI - the key part is making sure these changes are reflected on the main page
                this.storeLocator.renderSelectYourStore();

                // Important: Force update the main page elements - update the star and distance for all stores
                document.querySelectorAll('.my-location-result').forEach(el => {
                  el.dataset.active = 'false';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.remove('active');
                });

                // Reset drawer and hide
                this.searchLocations(true);
                this.hide();

                // Reload the page to ensure all elements are updated properly
                window.location.reload();
                return;
              }

              // Otherwise set as preferred store
              if (this.storeLocator.storageAvailable('localStorage')) {
                localStorage.setItem('preferredStoreId', location.id);
              }

              this.storeLocator.preferredStoreId = location.id;
              this.storeLocator.updateMyStoreWithLocation(location);

              // Update all stars on the main page immediately
              document.querySelectorAll('.my-location-result').forEach(el => {
                if (el.dataset.id === location.id) {
                  el.dataset.active = 'true';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.add('active');
                } else {
                  el.dataset.active = 'false';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.remove('active');
                }
              });

              // Force refresh location data with current geolocation to recalculate distances
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    // Recalculate distances using current position
                    this.storeLocator.fetchLocations(position, true, () => {
                      // Refresh the display after recalculating
                      this.searchLocations(true);
                      this.hide();

                      // Reload the page to ensure all elements are updated properly
                      window.location.reload();
                    });
                  },
                  () => {
                    // If geolocation fails, just refresh with existing data
                    this.searchLocations(true);
                    this.hide();

                    // Reload the page to ensure all elements are updated properly
                    window.location.reload();
                  }
                );
              } else {
                // If geolocation not available, just refresh with existing data
                this.searchLocations(true);
                this.hide();

                // Reload the page to ensure all elements are updated properly
                window.location.reload();
              }
            });
          }

          // Add existing accordion button listeners
          const accButton = this.results.querySelector(`[data-id="${location.id}"] .js-acc-button`);
          if (accButton) {
            accButton.addEventListener('click', this.handleAccordionClick.bind(this));
          }
        });

        let resultsText = '';
        if (nearbyValue === '') {
          if (this.storeLocator.myCoords) {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.resultsNearYou;
            } else {
              resultsText = this.resultsCount.dataset.resultNearYou;
            }
          } else {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.results;
            } else {
              resultsText = this.resultsCount.dataset.result;
            }
          }
        } else {
          if (resultCount > 1 || resultCount === 0) {
            resultsText = `${this.resultsCount.dataset.resultsNear} "${encodeHtml(nearbyValue)}"`;
          } else {
            resultsText = `${this.resultsCount.dataset.resultNear} "${encodeHtml(nearbyValue)}"`;
          }
        }
        this.resultsCount.innerHTML = `${resultCount} ${resultsText}`;
        if (resultCount === 0) {
          this.results.innerHTML = `
            <div class="my-location-result">
              <div class="my-location-result__address">${this.resultsCount.dataset.noResultsFound}</div>
            </div>
          `;
        }
        this.loading.classList.add('hide');
        this.results.scrollTop = 0;
      }

      selectPreferredLocation() {
        const activeResult = this.querySelector(this.dom.activeResult);

        if (activeResult) {
          const id = activeResult.dataset.id;

          // If there's already a preferred store
          if (id === this.storeLocator.preferredStoreId) {
            // Remove from localStorage
            if (this.storeLocator.storageAvailable('localStorage')) {
              localStorage.removeItem('preferredStoreId');
            }

            // Clear the preferred store ID
            this.storeLocator.preferredStoreId = null;

            // Update visual state
            activeResult.dataset.active = 'false';

            // Update UI
            this.storeLocator.renderSelectYourStore();

            // Reset drawer and hide
            this.searchLocations(true);
            this.hide();
            return;
          }

          // Otherwise set as preferred store
          if (this.storeLocator.storageAvailable('localStorage')) {
            localStorage.setItem('preferredStoreId', id);
          }

          const location = this.storeLocator.allLocationsData.find(loc => loc.id === id);
          if (location) {
            this.storeLocator.preferredStoreId = id;
            this.storeLocator.updateMyStoreWithLocation(location);
          }

          this.hide();
        }
      }
    },
  );
}

/**
 * Store Locator Drawer Web Component
 * Handles the store search/selection modal interface
 */
if (!customElements.get('my-store-locator-drawer')) {
  customElements.define(
    'my-store-locator-drawer',
    class MyStoreLocatorDrawer extends HTMLElement {
      constructor() {
        super();

        this.dom = {
          storeLocatorSearch: 'storeLocatorSearch',
          form: 'storeLocatorSearchForm',
          storeLocator: 'my-store-locator',
          suggestions: '.js-store-locator-search-suggestions',
          error: '.js-store-locator-search-error',
          loading: 'storeLocatorDrawerLoading',
          results: 'myLocationResults',
          resultsCount: '.js-store-locator-results-count',
          activeResult: '.js-my-location-result[data-active="true"]',
          resultsDistance: '.js-store-locator-results-distance',

          productInfo: '.js-store-locator-product-info',
          acc_button: '.js-acc-button',
          acc_icon: '.js-acc-icon',
          acc_details: '.js-acc-details',
        };
        this.cls = {
          active: 'is-active',
        };

        this.form = document.getElementById(this.dom.form);
        this.results = document.getElementById(this.dom.results);
        this.resultsCount = this.querySelector(this.dom.resultsCount);
        this.resultsDistance = this.querySelector(this.dom.resultsDistance);
        this.storeLocatorSearch = document.getElementById(this.dom.storeLocatorSearch);
        this.storeLocator = document.querySelector(this.dom.storeLocator);
        this.suggestions = this.querySelector(this.dom.suggestions);
        this.error = this.querySelector(this.dom.error);
        this.loading = document.getElementById(this.dom.loading);

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });

        this.init();

        // Bind the click outside handler
        this.handleClickOutside = this.handleClickOutside.bind(this);

        // Add click outside listener
        document.addEventListener('click', this.handleClickOutside);

        this.storeLocatorSearch.onkeyup = debounce(this.storeLocatorSearchOnKeyUp.bind(this), 200);
      }

      init() {
        const expiry = storeLocator.Data.locationCookieExpiry || 30;

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.searchLocations();
        });

        this.resultsDistance.addEventListener('change', () => {
          this.searchLocations(true);
        });

        this.storeLocatorSearch.onkeyup = debounce(this.storeLocatorSearchOnKeyUp.bind(this), 200);

        // Check if we're on the product page
        const isProductPage = document.querySelector('body').classList.contains('template-product');

        // Wait for locations data to be loaded before initializing search
        if (this.storeLocator.allLocationsData.length === 0) {
          const checkData = setInterval(() => {
            if (this.storeLocator.allLocationsData.length > 0) {
              clearInterval(checkData);
              if (!isProductPage) {
                // Initialize search immediately for store pages
                this.initializeSearch();
              } else {
                // For product pages, just show all locations without geocoding
                this.updateStoreDetails(this.storeLocator.allLocationsData, '');
              }
            }
          }, 100);
        } else {
          if (!isProductPage) {
            this.initializeSearch();
          } else {
            this.updateStoreDetails(this.storeLocator.allLocationsData, '');
          }
        }
      }

      initializeSearch() {
        // Only fill search field if preferred store exists
        if (this.storeLocator.preferredStoreId) {
          const preferredLocation = this.storeLocator.allLocationsData.find(
            (location) => location.id === this.storeLocator.preferredStoreId
          );

          if (preferredLocation) {
            this.storeLocatorSearch.value = preferredLocation.address.city;
          }
        } else {
          // Clear search field if no preferred store
          this.storeLocatorSearch.value = '';
        }

        // Show all stores
        this.searchLocations(true);
      }

      async storeLocatorSearchOnKeyUp(e) {
        const userData = e.target.value.trim();
        let emptyArray = [];
        if (userData && e.code.toUpperCase() !== 'ENTER') {
          if (!isValidStoreSearchTerm(userData)) {
            this.suggestions.classList.add('hide');
            return;
          }

          emptyArray = await this.getPlaceSuggestions(userData);
          if (emptyArray.length > 0) {
            const suggestionsHtml = emptyArray.map(
              (data) => `<button type="button">${encodeHtml(data)}</button>`,
            );
            this.suggestions.innerHTML = suggestionsHtml.join('');
            const allList = this.suggestions.querySelectorAll('button');
            allList.forEach((item) => {
              item.addEventListener('click', (e) => {
                this.selectSuggestion(e.target);
              });
            });
            this.suggestions.classList.remove('hide');
          } else {
            this.suggestions.classList.add('hide');
          }
        } else {
          this.suggestions.classList.add('hide');
        }
      }

      getPlaceSuggestions(address) {
        return new Promise((resolve, reject) => {
          const autocompleteService = new google.maps.places.AutocompleteService();
          const request = {
            input: address,
            // Restrict to Belgium and Luxembourg
            componentRestrictions: { country: ['BE', 'LU'] },
          };

          autocompleteService.getPlacePredictions(request, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              const suggestions = predictions.map(prediction => prediction.description);
              resolve(suggestions);
            } else {
              resolve([]);
            }
          });
        });
      }

      selectPreferredLocation() {
        const activeResult = this.querySelector(this.dom.activeResult);

        if (activeResult) {
          const id = activeResult.dataset.id;

          // If there's already a preferred store
          if (id === this.storeLocator.preferredStoreId) {
            // Remove from localStorage
            if (this.storeLocator.storageAvailable('localStorage')) {
              localStorage.removeItem('preferredStoreId');
            }

            // Clear the preferred store ID
            this.storeLocator.preferredStoreId = null;

            // Update visual state
            activeResult.dataset.active = 'false';

            // Update UI
            this.storeLocator.renderSelectYourStore();

            // Reset drawer and hide
            this.searchLocations(true);
            this.hide();
            return;
          }

          // Otherwise set as preferred store
          if (this.storeLocator.storageAvailable('localStorage')) {
            localStorage.setItem('preferredStoreId', id);
          }

          const location = this.storeLocator.allLocationsData.find(loc => loc.id === id);
          if (location) {
            this.storeLocator.preferredStoreId = id;
            this.storeLocator.updateMyStoreWithLocation(location);
          }

          this.hide();
        }
      }

      useLocation() {
        this.storeLocatorSearch.value = '';
        this.loading.classList.remove('hidden');
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            this.updateDrawerStoreDetails.bind(this),
            this.updateDrawerStoreDetails.bind(this),
          );
        } else {
          this.updateDrawerStoreDetails();
        }
      }

      updateDrawerStoreDetails(position, searchValue = null) {
        this.storeLocator.fetchLocations(position, true, () => {
          this.loading.classList.add('hide');
          // Use the provided search value instead of defaulting to the preferred store's city
          if (searchValue) {
            this.storeLocatorSearch.value = searchValue;
          }
          this.updateStoreDetails(this.storeLocator.allLocationsData, searchValue || '');
        });
      }

      selectSuggestion(element) {
        let selectData = element.textContent;
        this.storeLocatorSearch.value = selectData.trim();
        this.suggestions.classList.add('hide');
        this.searchLocations();
      }

      getObjects(obj, key, val) {
        const objects = obj.filter((location) => {
          const locationData = location.address;
          if (locationData && locationData[key]) {
            return locationData[key].toLowerCase() === val?.toLowerCase();
          } else {
            return null;
          }
        });
        return objects;
      }

      async searchLocations(noErrors = false) {
        let allLocationsData = this.storeLocator.allLocationsData;

        let searchLocation = this.storeLocatorSearch.value;
        searchLocation = searchLocation.trim();

        if (searchLocation && !isValidStoreSearchTerm(searchLocation)) {
          this.error.classList.remove('hide');
          this.suggestions.classList.add('hide');
          this.results.innerHTML = '';
          this.resultsCount.textContent = '';
          this.loading.classList.add('hide');
          return;
        }

        if (!noErrors && searchLocation !== '') {
          if (searchLocation === '') {
            this.error.classList.remove('hide');
            return;
          } else {
            this.error.classList.add('hide');
          }
        }

        this.loading.classList.add('hide');

        if (searchLocation) {
          const geocodedPosition = await this.geocodePosition(searchLocation);
          if (geocodedPosition) {
            this.updateDrawerStoreDetails(geocodedPosition, searchLocation);
            return;
          }
        }

        let storeDetails = [];
        if (searchLocation) {
          let searchCity = this.getObjects(allLocationsData, 'city', searchLocation);
          let searchZipcode = this.getObjects(allLocationsData, 'zip', searchLocation);
          let searchProvince = this.getObjects(allLocationsData, 'province', searchLocation);
          let country = this.getObjects(allLocationsData, 'country', searchLocation);

          if (
            searchCity.length > 0 ||
            searchZipcode.length > 0 ||
            searchProvince.length > 0 ||
            country.length > 0
          ) {
            if (searchCity.length > 0) {
              storeDetails = searchCity;
            } else if (searchProvince.length > 0) {
              storeDetails = searchProvince;
            } else if (searchZipcode.length > 0) {
              storeDetails = searchZipcode;
            } else if (country.length > 0) {
              storeDetails = country;
            }
          } else {
            if (!this.suggestions.classList.contains('hide')) {
              const firstSuggestion = this.suggestions.querySelector('button');
              if (firstSuggestion) {
                this.selectSuggestion(firstSuggestion);
                return;
              }
            }
            storeDetails = [];
          }
        } else {
          storeDetails = allLocationsData;
        }

        this.updateStoreDetails(storeDetails, searchLocation);
      }

      geocodePosition(address) {
        return new Promise((resolve, reject) => {
          // Check if Google Maps API is available
          if (typeof google === 'undefined' || !google.maps) {
            console.warn('Google Maps API not loaded, skipping geocoding');
            resolve(null);
            return;
          }

          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK') {
              const latitude = results?.[0]?.geometry?.location?.lat();
              const longitude = results?.[0]?.geometry?.location?.lng();
              if (latitude && longitude) {
                const position = {
                  coords: {
                    latitude,
                    longitude,
                  },
                  timestamp: Date.now(),
                };
                resolve(position);
              }
            }
            resolve(null);
          });
        });
      }

      updateStoreDetails(locationData, nearbyValue) {
        let resultCount = 0;
        const { mapPage } = storeLocator.Data || false;

        this.results.innerHTML = '';

        let filteredLocations = locationData;
        const hasDistanceData = locationData.some((location) => location.distance);

        if (this.resultsDistance.value !== 'all' && hasDistanceData) {
          const selectedDistance = parseFloat(this.resultsDistance.value);
          filteredLocations = locationData.filter((location) => {
            if (!location.distance) return false;
            const locationDistance = parseFloat(location.distance.split(' ')[0]);
            return locationDistance <= selectedDistance;
          });
        }

        // Sort locations by distance if distance data is available
        if (hasDistanceData) {
          filteredLocations.sort((a, b) => {
            const distanceA = a.distance ? parseFloat(a.distance.split(' ')[0]) : Infinity;
            const distanceB = b.distance ? parseFloat(b.distance.split(' ')[0]) : Infinity;
            return distanceA - distanceB;
          });
        } else if (!nearbyValue) {
          if (this.storeLocator.preferredStoreId) {
            // If preferred store exists, move it to top
            const activeIndex = filteredLocations.findIndex(
              (location) => location.id === this.storeLocator.preferredStoreId
            );
            if (activeIndex > -1) {
              filteredLocations.splice(0, 0, filteredLocations.splice(activeIndex, 1)[0]);
            }
          } else {
            // If no preferred store, sort alphabetically by name
            filteredLocations.sort((a, b) => a.name.localeCompare(b.name));
          }
        }

        if (window.showLocation) {
          window.showLocation(null, filteredLocations);
        }

        function setCityAndStateHTML(location) {
          const { address } = location || null;
          let html;

          if (!address) return;

          if (address?.city && address?.provinceCode) {
            html = `${address.city}, ${address.provinceCode}`;
            return html;
          }

          if (address?.city && !address?.provinceCode) {
            html = `${address.city}`;
            return html;
          }
        }

        filteredLocations.forEach((location) => {
          const isActive = this.storeLocator.preferredStoreId && location.id === this.storeLocator.preferredStoreId;
          const distanceHidden = location.distance ? '' : ' hidden';

          let availabilityHtml = '';
          if (location.available) {
            let stockHtml = '';

            let pickUpTimeTranslated = '';
            if (location.pickUpTime) {
              const key = location.pickUpTime;
              const languageKey = storeLocator.Data.language?.toLowerCase() || 'en';
              pickUpTimeTranslated =
                window.deliveryOptionsStrings?.[languageKey]?.[key] || location.pickUpTime;
            }

            if (location.quantityAvailable < 10) {
              const stockText = this.storeLocator.dataset.lowStockHtml.replace(
                '{{ quantity }}',
                location.quantityAvailable,
              );
              stockHtml = `
                <div class="my-location-result__availability-stock">${stockText}</div>
              `;
            }
            availabilityHtml = `
              <div class="my-location-result__availability">
                <div class="my-location-result__availability-time">${pickUpTimeTranslated}</div>
                ${stockHtml}
              </div>
            `;
          } else if (location.available === false) {
            availabilityHtml = `
              <div class="my-location-result__availability">
                ${this.storeLocator.dataset.unavailableText}
              </div>
            `;
          }
          let itemKeyAttr = '';
          if (location.itemKey) {
            itemKeyAttr = `data-item-key="${location.itemKey}"`;
          }
          let resultHtml;
          const { address, name } = location || null;
          if (!address) return;
          const { latitude, longitude } = address || null;

          let locationImageHtml = '';
          let serviceHtml = '';
          let telephoneVal = null;
          let calloutHref = null;
          let addressHref = null;

          if (location?.services_html) {
            serviceHtml = `
                <button class="my-location-result__services-btn js-acc-button" type="button">
                  <span>${
                    storeLocator?.Data?.storeServiceTrigger
                      ? storeLocator.Data.storeServiceTrigger
                      : 'View store services'
                  }</span>
                  <i class="myarrow fa fa-chevron-down my-location-result__services-icon js-acc-icon" aria-hidden="true"></i>
                </button>
                <div class="my-location-result__services js-acc-details" aria-expanded="false">${
                  location.services_html
                }</div>
              `;
          }

          if (location?.address?.phone) {
            telephoneVal = location.address.phone;
          }

          if (location?.phoneOverride?.value) {
            telephoneVal = location.phoneOverride.value;
          }

          const encodedName = encodeURIComponent(name);
          addressHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${address.latitude},${address.longitude}`;
          calloutHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${address.latitude},${address.longitude}`;

          // Build the location result HTML with store details, including distance, address, phone, and services
            resultHtml = `
            <div
              class="js-my-location-result my-location-result"
              data-active="${isActive}"
              data-id="${location.id}"
              data-name="${name}"
              data-latitude="${latitude}"
              data-longitude="${longitude}"
              aria-role="button"
              ${itemKeyAttr}
            >
              <div class="js-my-location-result-distance my-store-locator__details-distance">
                <div class="icon">
                  ${storeLocatorIcons.map}
                </div>
                ${location.distance ? location.distance : ''}
              </div>

              <div class="">
                <div class="my-location-result__my-store">${storeLocator.Data.myStore}</div>
                <section class="js-my-results-details">
                  <div class="my-location-result__details">
                    <div class="my-location-result__name">${location.name}</div>

                    </div>

                    <ul class="list-unstyled my-location-result__business">
                      ${
                        location?.address?.address1
                          ? `<li class="my-location-result__address">
                          <a href="${calloutHref}" target="_blank">
                            ${location.address.address1}${
                              location.address.zip ? `, ${location.address.zip}` : ''
                            } ${location.address.city}
                          </a>
                        </li>`
                          : ''
                      }
                      ${
                        telephoneVal || location?.email?.value
                          ? `<li class="my-location-result__contact-row my-location-result__address">
                            ${
                              telephoneVal
                                ? `<a class="my-location-result__contact-item" href="tel:${telephoneVal}">
                                    <div class="icon icon-phone">
                                      ${storeLocatorIcons.phone}
                                    </div>
                                    <span>${telephoneVal}</span>
                                  </a>`
                                : ''
                            }
                          </li>`
                          : ''
                      }
                      ${
                        location.hours?.value
                          ? `<li class="my-location-result__address ${
                              location.hours.isOpen ? 'store--open' : 'store--closed'
                            }">
                          <span class="store-status-indicator"></span>
                          <span>
                            ${
                              location.hours.isOpen
                                ? location.hours.value.startsWith('exceptional:')
                                  ? `${storeLocator.Data.nowExceptionallyOpen || 'Now Exceptionally Open'} ${
                                      storeLocator.Data.untilText || 'until'
                                    } ${location.hours.closingTime}`
                                  : `${storeLocator.Data.nowOpen || 'Now Open'} ${
                                      storeLocator.Data.untilText || 'until'
                                    } ${location.hours.closingTime}`
                                : location.hours.value === 'closed' || location.hours.value === 'gesloten' || location.hours.value === 'fermé'
                                  ? `${storeLocator.Data.nowClosed || 'Now Closed'}`
                                  : location.hours.value === 'exceptionally_closed'
                                    ? `${storeLocator.Data.exceptionallyClosedToday || 'Exceptionally Closed Today'}`
                                    : `${storeLocator.Data.nowClosed || 'Now Closed'} – ${
                                        storeLocator.Data.opensAt || 'opens at'
                                      } ${location.hours.openingTime}`
                            }
                          </span>
                        </li>`
                          : ''
                      }
                    </ul>

                  </section>
                  ${serviceHtml}

                   <div class="my-location-result__link js-my-location-link">

                     ${
                       location.storePageUrl
                         ? `
                          <a href="${location.storePageUrl}" class="button button--secondary">
                            <span data-button-animate-chars="" class="button__text">
                              ${storeLocator.Data.viewStorePage || 'View Store Page'}
                            </span>

                            <div class="icon icon-arrow-right">
                              ${theme.icons.arrowRightTopSmall}
                            </div>
                          </a>
                  `
                         : ''
                     }

                      <a href="${calloutHref}" target="_blank" rel="noopener noreferrer">
                          <div class="icon">
                            ${storeLocatorIcons.map}
                          </div>
                      </a>
                    </div>

                 <button class="js-make-my-store-btn my-location-result__make-my-store-btn ${isActive ? 'active' : ''}">
                    <div class="icon icon--star">
                      ${storeLocatorIcons.star}
                    </div>
                  </button>
              </div>


            </div>`;
            const resultNode = new DOMParser().parseFromString(resultHtml, 'text/html').body
              .childNodes[0];
            resultNode.querySelector('.js-make-my-store-btn').addEventListener('click', (e) => {
              const activeResult = this.querySelector(this.dom.activeResult);
              if (activeResult) {
                activeResult.dataset.active = 'false';
              }
              e.currentTarget.parentNode.dataset.active = 'true';

              this.selectPreferredLocation();
            });

            this.results.append(resultNode);
            resultCount++;

          // Add event listeners for the newly created elements
          const makeMyStoreBtn = this.results.querySelector(
            `[data-id="${location.id}"] .js-make-my-store-btn`,
          );
          if (makeMyStoreBtn) {
            makeMyStoreBtn.addEventListener('click', (event) => {
              event.preventDefault();
              event.stopPropagation();

              // Check if this is already the preferred store
              if (this.storeLocator.preferredStoreId && location.id === this.storeLocator.preferredStoreId) {
                // Clear the preferred store state
                if (this.storeLocator.storageAvailable('localStorage')) {
                  localStorage.removeItem('preferredStoreId');
                }

                // Clear the ID in memory
                this.storeLocator.preferredStoreId = null;

                // Update visual state
                const storeElement = this.results.querySelector(`[data-id="${location.id}"]`);
                if (storeElement) {
                  storeElement.dataset.active = 'false';
                }

                // Update UI - the key part is making sure these changes are reflected on the main page
                this.storeLocator.renderSelectYourStore();

                // Important: Force update the main page elements - update the star and distance for all stores
                document.querySelectorAll('.my-location-result').forEach(el => {
                  el.dataset.active = 'false';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.remove('active');
                });

                // Reset drawer and hide
                this.searchLocations(true);
                this.hide();

                // Reload the page to ensure all elements are updated properly
                window.location.reload();
                return;
              }

              // Otherwise set as preferred store
              if (this.storeLocator.storageAvailable('localStorage')) {
                localStorage.setItem('preferredStoreId', location.id);
              }

              this.storeLocator.preferredStoreId = location.id;
              this.storeLocator.updateMyStoreWithLocation(location);

              // Update all stars on the main page immediately
              document.querySelectorAll('.my-location-result').forEach(el => {
                if (el.dataset.id === location.id) {
                  el.dataset.active = 'true';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.add('active');
                } else {
                  el.dataset.active = 'false';
                  const starBtn = el.querySelector('.my-location-result__make-my-store-btn');
                  if (starBtn) starBtn.classList.remove('active');
                }
              });

              // Force refresh location data with current geolocation to recalculate distances
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    // Recalculate distances using current position
                    this.storeLocator.fetchLocations(position, true, () => {
                      // Refresh the display after recalculating
                      this.searchLocations(true);
                      this.hide();

                      // Reload the page to ensure all elements are updated properly
                      window.location.reload();
                    });
                  },
                  () => {
                    // If geolocation fails, just refresh with existing data
                    this.searchLocations(true);
                    this.hide();

                    // Reload the page to ensure all elements are updated properly
                    window.location.reload();
                  }
                );
              } else {
                // If geolocation not available, just refresh with existing data
                this.searchLocations(true);
                this.hide();

                // Reload the page to ensure all elements are updated properly
                window.location.reload();
              }
            });
          }

          // Add existing accordion button listeners
          const accButton = this.results.querySelector(`[data-id="${location.id}"] .js-acc-button`);
          if (accButton) {
            accButton.addEventListener('click', this.handleAccordionClick.bind(this));
          }
        });

        let resultsText = '';
        if (nearbyValue === '') {
          if (this.storeLocator.myCoords) {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.resultsNearYou;
            } else {
              resultsText = this.resultsCount.dataset.resultNearYou;
            }
          } else {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.results;
            } else {
              resultsText = this.resultsCount.dataset.result;
            }
          }
        } else {
          if (resultCount > 1 || resultCount === 0) {
            resultsText = `${this.resultsCount.dataset.resultsNear} "${encodeHtml(nearbyValue)}"`;
          } else {
            resultsText = `${this.resultsCount.dataset.resultNear} "${encodeHtml(nearbyValue)}"`;
          }
        }
        this.resultsCount.innerHTML = `${resultCount} ${resultsText}`;
        if (resultCount === 0) {
          this.results.innerHTML = `
            <div class="my-location-result">
              <div class="my-location-result__address">${this.resultsCount.dataset.noResultsFound}</div>
            </div>
          `;
        }
        this.loading.classList.add('hide');
        this.results.scrollTop = 0;
      }

      handleBodyClick(evt) {
        const target = evt.target;

        if (target.classList.contains('js-my-store-locator-search-drawer-launcher')) {
          return;
        }
        if (
          target !== this &&
          !target.closest('my-store-locator-drawer') &&
          !target.closest('.js-my-store-locator-btn') &&
          !target.classList.contains('js-my-store-locator-btn') &&
          !target.classList.contains('js-my-store-locator-drawer-btn')
        ) {
          this.hide();
        }
        this.suggestions.classList.add('hide');
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.documentElement.classList.remove('overflow-hidden');
      }

      show(focusElement) {
        // show and hide product data and locations based on itemKey
        if (focusElement.dataset?.itemKey) {
          const productsInfo = document.querySelectorAll(this.dom.productInfo);
          productsInfo.forEach((productInfo) => {
            if (productInfo.dataset.itemKey === focusElement.dataset.itemKey) {
              productInfo.classList.remove('hide');
            } else {
              productInfo.classList.add('hide');
            }
          });
        } else if (focusElement.dataset?.productInfo === 'false') {
          const productsInfo = document.querySelectorAll(this.dom.productInfo);
          productsInfo.forEach((productInfo) => {
            productInfo.classList.add('hide');
          });
        }

        this.setAttribute(
          'data-hide-availability',
          focusElement.dataset?.hideAvailability || false,
        );

        this.focusElement = focusElement;

        // If on product page, initialize search when modal opens
        const isProductPage = document.querySelector('body').classList.contains('template-product');
        if (isProductPage) {
          if (this.storeLocator.preferredStoreId) {
            const preferredLocation = this.storeLocator.allLocationsData.find(
              (location) => location.id === this.storeLocator.preferredStoreId,
            );

            if (preferredLocation) {
              this.storeLocatorSearch.value = preferredLocation.address.city;
              this.searchLocations(true);
            }
          } else {
            this.searchLocations(true);
          }
        }

        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.documentElement.classList.add('overflow-hidden');
      }

      handleClickOutside(event) {
        if (this.suggestions && !this.suggestions.classList.contains('hide')) {
          const isClickInside =
            this.suggestions.contains(event.target) ||
            this.storeLocatorSearch.contains(event.target);

          if (!isClickInside) {
            this.suggestions.classList.add('hide');
          }
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleClickOutside);
      }
    },
  );
}

document.addEventListener('DOMContentLoaded', function() {
  const mapTab = document.querySelector('.js-store-locator-tab-map');
  const listTab = document.querySelector('.js-store-locator-tab-list');
  const mapWrapper = document.getElementById('mobileMapWrapper');
  const resultsWrapper = document.getElementById('myLocationResults');
  const isMobile = () => window.innerWidth < 768;

  // Immediately set initial display states to prevent flash of content
  if (mapWrapper && resultsWrapper) {
    if (isMobile()) { // Mobile check
      // On mobile, ensure results are hidden initially
      resultsWrapper.style.display = 'none';
      mapWrapper.style.display = 'block';
    } else {
      // On desktop, both can be visible
      resultsWrapper.style.display = 'block';
      mapWrapper.style.display = 'block';
    }
  }

  function toggleActiveTab(activeTab, inactiveTab, showElement, hideElement) {
    activeTab.setAttribute('data-active', 'true');
    inactiveTab.setAttribute('data-active', 'false');

    if (isMobile()) {
      // On mobile, toggle visibility as normal
      showElement.style.display = 'block';
      hideElement.style.display = 'none';
    } else {
      // On desktop, ensure both are visible
      showElement.style.display = 'block';
      hideElement.style.display = 'block';
    }
  }

  // Handle window resize events to adjust display for mobile/desktop
  window.addEventListener('resize', function() {
    if (!mapWrapper || !resultsWrapper) return;

    if (isMobile()) {
      // On mobile, show only the active tab content
      if (mapTab.getAttribute('data-active') === 'true') {
        mapWrapper.style.display = 'block';
        resultsWrapper.style.display = 'none';
      } else {
        mapWrapper.style.display = 'none';
        resultsWrapper.style.display = 'block';
      }
    } else {
      // On desktop, show both
      mapWrapper.style.display = 'block';
      resultsWrapper.style.display = 'block';
    }
  });

  if (mapTab && listTab && mapWrapper && resultsWrapper) {
    mapTab.addEventListener('click', function() {
      toggleActiveTab(mapTab, listTab, mapWrapper, resultsWrapper);
    });

    listTab.addEventListener('click', function() {
      toggleActiveTab(listTab, mapTab, resultsWrapper, mapWrapper);
    });

    // Ensure results are loaded when store locator is initialized
    // This will be triggered once the map component is fully loaded
    const storeLocator = document.querySelector('my-store-locator');
    if (storeLocator) {
      const checkLoaded = setInterval(() => {
        if (storeLocator.allLocationsData && storeLocator.allLocationsData.length > 0) {
          clearInterval(checkLoaded);

          // Initialize drawer with locations data if it exists
          const drawer = document.querySelector('my-store-locator-drawer');
          if (drawer && drawer.updateStoreDetails && typeof drawer.updateStoreDetails === 'function') {
            drawer.updateStoreDetails(storeLocator.allLocationsData, '');
          }
        }
      }, 500);
    }
  }
});
